<template>
  <v-card v-if="showView">
    <!-- Loading bar -->
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      absolute
      top
      color="primary"
    ></v-progress-linear>
    <v-container fluid>
      <v-row class="pt-2" align="center">
        <!-- Text input filter -->
        <v-col
          ><v-text-field
            ref="freehandFilter"
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Serial number, Order number, Product or Tenant"
            dense
            hide-details
            clearable
            :rules="searchRules"
            @keyup.enter="onSearch"
            @click:clear="onClearSearchField"
          ></v-text-field
        ></v-col>
        <!-- Export to CSV button with hover text-->
        <v-col v-if="canExportCSV" cols="auto" class="d-flex justify-end"
          ><v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }"
              ><v-btn
                text
                icon
                small
                color="primary"
                :disabled="isLoading"
                v-bind="attrs"
                v-on="on"
                @click="exportToCsv"
                ><v-icon>mdi-export</v-icon>
              </v-btn></template
            >
            <span>Export to CSV</span>
          </v-tooltip></v-col
        >
      </v-row>
      <!-- Dropdown filters -->
      <v-row>
        <!-- Products select -->
        <v-col cols="auto">
          <TreeSelect
            :label="$t('machines.dataTable.headers.product')"
            :items="availableProducts"
            :value.sync="treeSelectedItems"
            @update:treeSelectedItems="onProductsSelection"
        /></v-col>
        <!-- Countries select -->
        <v-col cols="auto"><CountryFilterMenu @input="onCountrySelect"/></v-col>
        <!-- Segments select -->
        <v-col cols="auto">
          <TreeSelect
            label="Segments"
            :items="availableSegments"
            :value.sync="treeSelectedItems"
            @update:treeSelectedItems="onSegmentsSelection"
        /></v-col>
        <!-- Date select -->
        <v-col cols="2">
          <v-select
            :items="timeRangeItems"
            item-value="value"
            item-text="text"
            v-model="selectedTimeRange"
            label="Delivery date"
            @change="onTimeRange"
            @click:clear="onClearTimeRange"
            clearable
            append-icon="mdi-chevron-down"
          ></v-select>
        </v-col>
        <v-col cols="2">
          <!-- Custom date -->
          <v-menu
            ref="datePickerMenu"
            v-model="datePickerMenu"
            :close-on-content-click="false"
            :return-value.sync="dates"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="dateRangeText"
                readonly
                :placeholder="$t('common.placeholders.selectDates')"
                :disabled="selectedTimeRange !== 'custom'"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="dates" range>
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="onClearTimeRange"
                >Cancel</v-btn
              >
              <v-btn
                text
                color="primary"
                :disabled="dates[0] === undefined || dates[1] === undefined"
                @click="$refs.datePickerMenu.save(dates)"
                >OK</v-btn
              >
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <!-- Machines table -->
      <v-row
        ><v-col
          ><v-data-table
            :headers="headers"
            :items="dataTableItems"
            :items-per-page="50"
            :footer-props="{
              'items-per-page-options': [5, 10, 20, 50],
              'items-per-page': 50
            }"
            sort-by="serialNo"
            sort-asc
            class="no-pointer-change"
          >
            <template v-slot:[`item.image`]="{ item }">
              <v-avatar class="ma-1">
                <v-img
                  :src="machineImageSrc(item)"
                  onerror="this.onerror=null;this.src='/products/jpg/default.jpg';this.title='Missing product image'"
                  :alt="item.displayedProductName"
                />
              </v-avatar>
            </template>

            <template v-slot:[`item.latestEngineHours`]="{ item }">
              {{ formatEngineHours(item.latestEngineHours) }}
            </template>

            <template v-slot:[`item.deliveryDate`]="{ item }">
              {{ formatDate(item.deliveryDate) }}
            </template>
          </v-data-table></v-col
        ></v-row
      >
    </v-container>
  </v-card>
</template>
<script>
import TreeSelect from "@/components/filters/TreeSelect"
import Formatter from "@/utils/formatter"
import Exporter from "@/utils/csvexport"
import CountryFilterMenu from "@/components/filters/CountryFilterMenu"

export default {
  props: ["tenantUid"],
  components: {
    CountryFilterMenu,
    TreeSelect
  },
  created() {
    this.$store.dispatch("loadSegmentMetadata", "en")
  },
  data() {
    return {
      isLoading: false,
      allFilters: {
        FilterTenantUid: "",
        SearchString: "",
        ProductIds: "",
        Countries: "",
        Start: "",
        End: "",
        Segments: []
      },
      // Freehand filter
      search: "",
      searchRules: [
        v =>
          /^[A-Za-z0-9 +]*$/.test(v) ||
          "Only letters, numbers, and + are allowed"
      ],
      // Segments filter
      treeSelectedItems: [],
      // Products filter
      products: [],
      // Date pickers
      dates: [],
      datePickerMenu: false,
      selectedTimeRange: null,
      dateRangeText: "",
      timeRangeItems: [
        { text: this.$t("common.enums.day.today"), value: "0" },
        {
          text: `${this.$t("common.filters.last")} ${this.$tc(
            "common.units.day",
            7
          )}`,
          value: "7"
        },
        {
          text: `${this.$t("common.filters.last")} ${this.$tc(
            "common.units.day",
            30
          )}`,
          value: "30"
        },
        {
          text: `${this.$t("common.filters.last")} ${this.$tc(
            "common.units.day",
            90
          )}`,
          value: "90"
        },
        { text: this.$t("common.filters.customRange"), value: "custom" }
      ],
      // Table
      headers: [
        {
          sortable: false,
          text: "",
          value: "image",
          width: 20
        },
        {
          text: "Product",
          value: "productName",
          align: "start"
        },
        {
          text: "Serial number",
          value: "serialNo",
          align: "start"
        },
        {
          text: "Order number",
          value: "orderNo",
          align: "start"
        },
        {
          text: "Delivery date",
          value: "deliveryDate",
          align: "start"
        },
        {
          text: "Engine hours",
          value: "latestEngineHours",
          align: "start"
        },
        {
          text: "Tenant",
          value: "tenantName",
          align: "start"
        },
        {
          text: "Country",
          value: "country",
          align: "start"
        },
        {
          text: "Segment",
          value: "segmentName",
          align: "start"
        },
        {
          text: "Category",
          value: "categoryName",
          align: "start"
        },
        {
          text: "Application",
          value: "applications",
          align: "start",
          sortable: false
        }
      ]
    }
  },
  computed: {
    showView() {
      return this.$store.getters.canReadFleetAnalytics
    },
    canExportCSV() {
      return this.$store.getters.isAdmin
    },
    dataTableItems() {
      let storeData = this.$store.getters.fleetAnalyticsMachines
      if (storeData != [] && storeData?.length > 0) {
        return storeData
      }
      return []
    },
    // Combine ID assignment and normalization to fill the TreeSelect
    availableSegments() {
      let adjustedItems = this.addIdsAndNormalizeSegments(
        this.$store.getters.segmentMetadata
      )
      return adjustedItems
    },
    availableProducts() {
      let adjustedItems = this.addIdsAndNormalizeProducts(
        this.$store.getters.grouppedProducts
      )
      return adjustedItems
    },
    selectedTenant() {
      return this.$store.getters.selectedTenant.uid
    }
  },
  methods: {
    async fetchData() {
      try {
        this.isLoading = true
        await this.$store.dispatch("fetchFleetAnalyticsMachines", {
          tenantUid: this.tenantUid,
          filters: this.allFilters
        })
      } catch (err) {
        this.showErrorMessage(
          "faied to fetch machines for fleet analytics " + err
        )
        this.isLoading = false
      }
      this.isLoading = false
    },
    showErrorMessage(msg) {
      this.showError = true
      this.errorText = msg
    },
    onClearSearchField() {
      this.allFilters.SearchString = ""
      this.onApplyFilters()
    },
    onClearTimeRange() {
      this.datePickerMenu = false
      this.allFilters.Start = ""
      this.allFilters.End = ""
      this.dates = []
      this.selectedTimeRange = null
      this.onApplyFilters()
    },
    onCountrySelect(selection) {
      this.allFilters.Countries = selection.join(",")
    },
    onSegmentsSelection(selection) {
      this.allFilters.Segments = this.convertSelectedSegmentsfunction(
        selection,
        this.availableSegments
      )
    },
    onProductsSelection(selection) {
      const selectionAsArray = Array.from(selection)
      const selectionAsString = selectionAsArray.join(", ")
      this.allFilters.ProductIds = selectionAsString
    },
    // Recursive function: assign hierarchical IDs and normalize to {id, name, children}
    addIdsAndNormalizeSegments(items, path = "") {
      return items.map((item, index) => {
        const id = path ? `${path}.${index + 1}` : `${index + 1}`

        // Determine children: first level uses categories, second level uses applications
        let children = item.categories || item.applications
        // If children are strings (leaf nodes), convert them into objects
        if (children && typeof children[0] === "string") {
          children = children.map((name, i) => ({
            name,
            id: `${id}.${i + 1}`,
            children: []
          }))
        }
        return {
          ...item,
          id, // hierarchical ID
          name: item.name,
          children:
            children && children.length
              ? this.addIdsAndNormalizeSegments(children, id)
              : [] // empty array = treated as leaf by TreeSelect
        }
      })
    },
    convertSelectedSegmentsfunction(filteredValues, source) {
      const result = []

      for (const path of filteredValues) {
        const parts = path.split(".").map(Number)

        const segmentId = parts[0]
        const categoryId = parts[1]
        const applicationIndex = parts[2]

        // CASE 1: only ["5"]
        if (parts.length === 1) {
          result.push({ segmentId: segmentId })
          continue
        }

        const segment = source.find(x => x.id == segmentId)
        if (!segment) {
          continue
        }

        const categories = segment.categories?.find(x => x.id == categoryId)
        if (!categories) {
          continue
        }

        let output = {
          segmentId: segmentId,
          categoryId: categoryId
        }

        // CASE 3: ["2.1.3"]
        if (parts.length === 3) {
          const value = categories.applications?.[applicationIndex - 1]
          if (value === undefined) {
            continue
          }
          output.applications = value
        }

        // CASE 2: ["2.1"] → do nothing extra
        result.push(output)
      }
      return result
    },
    // addIdsAndNormalizeProducts(data) {
    //   return data.map(this.normalizeNode)
    // },

    // normalizeNode(node) {
    //   return {
    //     id: node.productId,
    //     name: node.productName,
    //     children: (node.items || []).map(this.normalizeNode)
    //   }
    // },
    addIdsAndNormalizeProducts(items) {
      //Mapping products to have id, name and children.
      return items.map(group => ({
        id: group.productId,
        name: group.productName,
        children: group.items.map(item => ({
          id: item.productId,
          name: item.productName
        }))
      }))
    },
    formatDate(date) {
      return Formatter(this.$i18n).formatDate(date)
    },
    formatEngineHours(hours) {
      if (hours == undefined || hours == 0) {
        return "-"
      }
      return hours + " h"
    },
    machineImageSrc(item) {
      if (item.isSpp) {
        return `/products/jpg/${item.productId}_SPP.jpg`
      }
      return `/products/jpg/${item.productId}.jpg`
    },
    // Validation: only check if string is non-empty or array has items
    isValidValue(value) {
      if (value === null || value === undefined) {
        return false
      }

      if (typeof value === "string") {
        return value !== "" // non-empty string
      }

      if (Array.isArray(value)) {
        return value.length > 0 // array has at least one item
      }

      return false // anything else is invalid
    },
    // Remove filter attributes without value
    cleanFilterData() {
      const cleanedFilterData = {}
      // Loop through each field in form
      for (const key in this.allFilters) {
        const value = this.allFilters[key]
        // Only include valid fields
        if (this.isValidValue(value)) {
          cleanedFilterData[key] = value
        }
      }
      return cleanedFilterData
    },
    async onApplyFilters() {
      this.cleanFilterData() // Remove empty filtervalues
      await this.fetchData()
    },
    onSearch() {
      if (this.$refs.freehandFilter.validate()) {
        this.allFilters.SearchString = this.search
      }
    },
    onTimeRange() {
      if (this.selectedTimeRange != "custom") {
        this.allFilters.Start = this.selectedTimeRange
      }
    },
    exportToCsv() {
      // Make application a string instead
      let machinesAsString = this.dataTableItems.map(d => ({
        ...d,
        applications: Array.isArray(d.applications)
          ? `"${d.applications.join(", ")}"`
          : d.applications && d.applications.length
          ? d.applications
          : ""
      }))
      // Make a copy of the header items, and remove the first one (image)
      let csvHeaderItems = [...this.headers]
      csvHeaderItems.splice(0, 1)

      const rows = Exporter(this.$i18n).exportFleetAnalyticsTable(
        csvHeaderItems,
        machinesAsString
      )
      this.snackbarText = this.$t("common.messages.recordsExported", {
        number: rows
      })
      this.snackbar = true
    }
  },
  watch: {
    tenantUid: {
      immediate: true, // created already handled first load
      handler() {
        this.allFilters.FilterTenantUid = this.tenantUid
      }
    },
    allFilters: {
      immediate: true, // Instead of at (created())
      handler() {
        this.onApplyFilters()
      },
      deep: true
    },
    selectedTimeRange(newRange) {
      // Display date picker. OK callback will trigger fetch
      if (newRange == "custom") {
        this.datePickerMenu = true
      }
    },
    dates(newDates) {
      // Always set earliest date as start date
      if (!newDates || newDates.length !== 2) {
        this.dateRangeText = ""
        return
      }
      if (newDates[0] > newDates[1]) {
        newDates.reverse()
      }
      this.dateRangeText = newDates.join(" ~ ")

      this.allFilters.Start = newDates[0].toString()
      this.allFilters.End = newDates[1].toString()
      this.onApplyFilters()
    }
  }
}
</script>
<style>
/* To stop cursor/pointer from changing on table hover*/
.no-pointer-change .v-data-table__wrapper tr {
  cursor: default !important;
}
</style>
